import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { Sheet } from '../components/Sheet'
import {
  MoneyField, DateField, SelectField, TextField,
} from '../components/Fields'
import { PhotoField } from '../components/PhotoField'
import { ConfirmDelete } from '../components/ConfirmDelete'
import { useAuth } from '../auth/useAuth'
import { canDelete } from '../lib/types'
import { formatMoney, defaultPeriodForYear, periodsOfYear, formatPeriod } from '../lib/format'
import type { BankAccount, BankDepositRow } from './BankScreen'

/**
 * Saisie et modification d'un versement en banque.
 *
 * LE BORDEREAU EST OBLIGATOIRE. La colonne slip_document est NOT NULL
 * dans la base : sans photo, la ligne ne peut pas exister. Le bouton
 * d'enregistrement reste donc inactif tant que la piece n'est pas
 * jointe, plutot que d'envoyer une requete vouee a etre refusee. La
 * meme regle vaut en modification : on peut remplacer le bordereau,
 * jamais le retirer.
 *
 * C'est ici que se joint la preuve du mois, et non sur chaque loyer :
 * les loyers sont encaisses puis verses en une seule fois.
 *
 * Le gerant et l'assistant modifient, le proprietaire seul supprime :
 * c'est la RLS de la base qui le tient, l'ecran ne fait que ne pas
 * proposer un bouton voue au refus. Chaque modification et chaque
 * suppression laissent la ligne entiere dans le journal d'audit.
 */
export function BankDepositSheet({
  year,
  accounts,
  deposit = null,
  locked = false,
  lockedReason = null,
  onClose,
  onSaved,
  onDeleted,
}: {
  year: number
  accounts: BankAccount[]
  /** Versement existant a modifier. Absent : nouveau versement. */
  deposit?: BankDepositRow | null
  /** Mois cloture : plus rien ne bouge, dans un sens ni dans l'autre. */
  locked?: boolean
  lockedReason?: string | null
  onClose: () => void
  onSaved: () => void
  onDeleted?: () => void
}) {
  const { t, lang } = useI18n()
  const { profile } = useAuth()
  const peutSupprimer = canDelete(profile?.role ?? null)

  const [period, setPeriod] = useState(
    () => deposit?.period ?? defaultPeriodForYear(year),
  )
  const [accountId, setAccountId] = useState(
    deposit?.account_id ?? accounts[0]?.id ?? '',
  )
  const [depositedOn, setDepositedOn] = useState(
    deposit?.deposited_on ?? new Date().toISOString().slice(0, 10),
  )
  const [deposited, setDeposited] = useState<number | null>(
    deposit?.amount_deposited ?? null,
  )
  const [stampDuty, setStampDuty] = useState<number | null>(
    deposit?.stamp_duty ?? null,
  )
  const [credited, setCredited] = useState<number | null>(
    deposit?.amount_credited ?? null,
  )
  // En modification, le montant credite vient du releve de la banque :
  // il est deja « touche ». Sans cela, le recalcul depose moins timbre
  // ecraserait a l'ecran un chiffre que la banque a tranche.
  const [creditedTouched, setCreditedTouched] = useState(deposit !== null)
  const [remitter, setRemitter] = useState(deposit?.remitter ?? '')
  const [slipDoc, setSlipDoc] = useState<string | null>(
    deposit?.slip_document ?? null,
  )

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Le montant credite vaut en general le montant depose moins le
  // timbre. On le propose, sans l'imposer : c'est le releve de la
  // banque qui fait foi, pas ce calcul.
  const crediteSuggere =
    deposited === null ? null : Math.max(0, deposited - (stampDuty ?? 0))
  const crediteEffectif = creditedTouched ? credited : crediteSuggere

  const valide =
    !locked &&
    accountId !== '' &&
    deposited !== null &&
    deposited > 0 &&
    crediteEffectif !== null &&
    slipDoc !== null

  async function submit() {
    if (!valide) return
    setError(null)
    setSubmitting(true)

    const payload = {
      period,
      account_id: accountId,
      deposited_on: depositedOn,
      amount_deposited: deposited,
      amount_credited: crediteEffectif,
      stamp_duty: stampDuty ?? 0,
      // La reference du bordereau a ete retiree du formulaire : la
      // photo du bordereau tient lieu de reference, et un numero
      // recopie a la main serait une occasion d'erreur de plus.
      reference: null,
      remitter: remitter.trim() || null,
      slip_document: slipDoc,
    }

    const { error: e } = deposit
      ? await supabase.from('bank_deposits').update(payload).eq('id', deposit.id)
      : await supabase.from('bank_deposits').insert(payload)

    setSubmitting(false)
    if (e) {
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved()
  }

  return (
    <Sheet
      title={deposit ? t('bank_edit') : t('bank_add')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('save')}
      submitting={submitting}
      disabled={!valide}
      error={error}
    >
      {/* Mois cloture : le declencheur trg_lock_bank_deposits refuserait
          l'ecriture comme la suppression. On le dit avant d'essayer. */}
      {locked && lockedReason && (
        <div className="banner banner--warn">
          <strong>{lockedReason}</strong>
        </div>
      )}
      {/* La photo d'abord : on est devant le bordereau. */}
      <PhotoField
        label={t('bank_slip')}
        required
        target={{ kind: 'bordereau', period }}
        documentId={slipDoc}
        onUploaded={setSlipDoc}
      />

      {!slipDoc && (
        <div className="banner banner--error">
          <strong>{t('bank_slip_required')}</strong>
          <span>{t('bank_slip_required_body')}</span>
        </div>
      )}

      <SelectField
        label={t('report_period')}
        required
        value={period}
        onChange={setPeriod}
        options={periodsOfYear(year).map((p) => ({
          value: p,
          label: formatPeriod(p, lang),
        }))}
      />

      <SelectField
        label={t('bank_account')}
        required
        value={accountId}
        onChange={setAccountId}
        options={accounts.map((a) => ({
          // Nom de banque issu de la base, jamais traduit.
          value: a.id,
          label: a.branch ? `${a.name} — ${a.branch}` : a.name,
        }))}
      />

      <DateField
        label={t('bank_deposited_on')}
        required
        value={depositedOn}
        onChange={setDepositedOn}
      />

      <MoneyField
        label={t('bank_deposited')}
        hint={t('bank_deposited_hint')}
        required
        value={deposited}
        onChange={setDeposited}
      />

      <MoneyField
        label={t('bank_stamp_duty')}
        hint={t('bank_stamp_duty_hint')}
        value={stampDuty}
        onChange={setStampDuty}
      />

      <MoneyField
        label={t('bank_credited')}
        hint={t('bank_credited_hint')}
        required
        value={crediteEffectif}
        onChange={(v) => {
          setCreditedTouched(true)
          setCredited(v)
        }}
      />

      {/* Le detail du calcul, visible, pour que le chiffre soit
          verifiable sans calculatrice. */}
      {deposited !== null && (
        <div className="calcbox">
          <span>
            {t('bank_deposited')} <strong>{formatMoney(deposited)}</strong>
          </span>
          <span>
            {t('bank_stamp_duty')} <strong>− {formatMoney(stampDuty ?? 0)}</strong>
          </span>
          <span>
            {t('bank_credited_expected')}{' '}
            <strong>{formatMoney(crediteSuggere ?? 0)}</strong>
          </span>
        </div>
      )}

      <TextField
        label={t('bank_remitter')}
        hint={t('bank_remitter_hint')}
        value={remitter}
        onChange={setRemitter}
      />

      {/* Suppression, reservee au proprietaire par la RLS. La photo du
          bordereau n'est PAS supprimee avec la ligne : elle reste
          consultable dans Documents. Un versement se supprime en
          general parce qu'il fait doublon ou qu'il est mal saisi,
          alors que le bordereau est une piece de banque reelle. */}
      {deposit && peutSupprimer && (
        <ConfirmDelete
          label={t('bank_delete')}
          question={t('bank_delete_confirm', {
            amount: formatMoney(deposit.amount_credited),
          })}
          disabled={submitting}
          blockedReason={locked ? lockedReason : null}
          onDelete={async () => {
            const { error: e } = await supabase
              .from('bank_deposits')
              .delete()
              .eq('id', deposit.id)
            if (e) throw new Error(e.message)
            onDeleted?.()
          }}
        />
      )}
    </Sheet>
  )
}
